import React from 'react';
import { connect } from '../../../../redux/store';
import {
  ErrorMessage,
  EmptyMessage,
  LoadingMessage,
  StaticTypeahead,
  C,
} from '@kineticdata/bundle-common';
import { I18n } from '@kineticdata/react';
import { Map } from 'immutable';
import { DefaultActions } from './';
import { CONTENT_MANAGER_MODE } from '../helpers';
import { ServiceCard } from '../../ServiceCard';

const mapStateToProps = (state, props) => {
  const isPublicMode = props.mode === CONTENT_MANAGER_MODE.PUBLIC;
  const isViewMode = props.mode !== CONTENT_MANAGER_MODE.EDIT;
  const id = props.activeItem && props.activeItem.id;
  const services = (isPublicMode
    ? state.app.catalog.services
    : state.settingsService.dataList
  ).filter(
    s => s.values[C.FIELD_SERVICE_ID] !== props.values.get(C.FIELD_SERVICE_ID),
  );

  return {
    isPublicMode,
    isViewMode,
    id,
    services,
    servicesLoading: isPublicMode
      ? !state.app.catalog.init && state.app.catalog.loading
      : !state.settingsService.dataInit && state.settingsService.dataLoading,
    servicesError: isPublicMode
      ? state.app.catalog.errors.first()
      : state.settingsService.dataError,
    servicesNameMap: services.reduce(
      (map, p) => map.set(p.values[C.FIELD_SERVICE_ID], p.values['Name']),
      Map(),
    ),
    dependencies: services.filter(s =>
      props.values
        .get('Dependent Service Ids', [])
        .includes(s.values[C.FIELD_SERVICE_ID]),
    ),
  };
};

const ServiceDependenciesRenderer = connect(mapStateToProps)(
  ({
    mode,
    isPublicMode,
    isViewMode,
    content,
    values,
    activeItem,
    id,
    services,
    servicesLoading,
    servicesError,
    servicesNameMap,
    dependencies,
    ...props
  }) => {
    return !!activeItem ? (
      <>
        <h2>
          <I18n>{activeItem.title}</I18n>
        </h2>

        {!isViewMode && (
          <div className="form mb-3">
            <div className="form-group">
              <label htmlFor="title">
                <I18n>Section Title</I18n>
              </label>
              <input
                type="text"
                id="title"
                value={activeItem.title}
                onChange={props.handleContentChange(id, 'title')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="service-dependencies" className="field-label">
                <I18n>Dependencies</I18n>
              </label>
              <StaticTypeahead
                valueProp="id"
                labelProp="name"
                options={services.toJS().map(p => ({
                  id: p.values[C.FIELD_SERVICE_ID],
                  name: p.values['Name'],
                }))}
                value={values.get('Dependent Service Ids', []).map(id => ({
                  id,
                  name: servicesNameMap.get(id) || `${id} (Invalid Service)`,
                }))}
                onChange={vals =>
                  props.handleValuesChangeDirect(id, 'Dependent Service Ids')(
                    vals.toJS().map(kw => kw.id),
                  )
                }
                search={{ fields: [{ name: 'name', operator: 'matches' }] }}
                components={{
                  Input: isViewMode
                    ? (values.get(C.FIELD_PORTFOLIO_ID) || []).length > 0
                      ? () => null
                      : () => (
                          <div className="form-control-plaintext">
                            <em>None</em>
                          </div>
                        )
                    : undefined,
                }}
                multiple={true}
                minSearchLength={0}
                id="service-dependencies"
                disabled={isViewMode}
              />
            </div>
          </div>
        )}

        {servicesError ? (
          <ErrorMessage
            title="The dependencies failed to load."
            message={servicesError.message}
          />
        ) : servicesLoading ? (
          <LoadingMessage />
        ) : dependencies.size === 0 ? (
          <EmptyMessage title="There are no service dependencies." />
        ) : (
          <div className="cards">
            {dependencies.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                path={
                  isViewMode
                    ? `../${service.values[C.FIELD_SERVICE_ID]}`
                    : undefined
                }
              />
            ))}
          </div>
        )}

        {!isViewMode && (
          <DefaultActions
            id={id}
            isDirty={activeItem.isDirty}
            handleResetSection={props.handleResetSection}
            handleContentDelete={props.handleContentDelete}
            actionsPending={props.actionsPending}
          />
        )}
      </>
    ) : null;
  },
);

// Metadata for this renderer
ServiceDependenciesRenderer.ID = 'SERVICE_DEPENDENCIES';
ServiceDependenciesRenderer.NAME = 'Service Dependencies';
ServiceDependenciesRenderer.TYPES = ['Service'];

// Define fields managed by this section so we can know which fields to reset
// when the section is reset. Can store additional metadata for fields to use in
// the renderer above.
ServiceDependenciesRenderer.FIELDS = {
  'Dependent Service Ids': {},
};

export default ServiceDependenciesRenderer;
